import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../../services/api';
import { apiGet } from '../../../../api/axiosClient';
import { useRealTimeChat } from '../../../chat/context/RealTimeChatContext';

export const analysisSteps = [
    { percent: 10, title: 'Analyzing document structure and extracting text...', desc: '' },
    { percent: 30, title: 'Initial document analysis complete...', desc: '' },
    { percent: 40, title: 'Processing document layout and formatting...', desc: '' },
    { percent: 50, title: 'Applying AI classification to extracted content...', desc: '' },
    { percent: 55, title: 'Extracting key project requirements and metadata...', desc: '' },
    { percent: 70, title: 'Synthesizing strategic context and deliverables...', desc: '' },
    { percent: 85, title: 'Drafting comprehensive analytical chapters...', desc: '' },
    { percent: 100, title: 'Analysis and extraction finalized successfully!', desc: '' }
];

const formatIndian = (numStr) => {
    if (!numStr) return '';
    let lastThree = numStr.substring(numStr.length - 3);
    let otherDigits = numStr.substring(0, numStr.length - 3);
    if (otherDigits !== '') {
        lastThree = ',' + lastThree;
    }
    return otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
};

const formatInternational = (numStr) => {
    if (!numStr) return '';
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatValuation = (value, curr) => {
    if (value === undefined || value === null) return '';
    const rawVal = String(value).replace(/[^0-9.]/g, '');
    const dotIndex = rawVal.indexOf('.');
    let integerPart = rawVal;
    let decimalPart = '';
    if (dotIndex !== -1) {
        integerPart = rawVal.slice(0, dotIndex);
        decimalPart = rawVal.slice(dotIndex + 1);
    }

    let formattedInteger = '';
    if (curr === 'IND') {
        formattedInteger = formatIndian(integerPart);
    } else {
        formattedInteger = formatInternational(integerPart);
    }

    let result = formattedInteger;
    if (dotIndex !== -1) {
        result += '.' + decimalPart;
    }
    return result;
};

export const useRFPIntake = () => {
    const navigate = useNavigate();
    const { subscribeToMessages } = useRealTimeChat();
    const fileInputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [rawFiles, setRawFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [activeStepIndex, setActiveStepIndex] = useState(0);
    const [currentRunId, setCurrentRunId] = useState(null);
    const [steps, setSteps] = useState(analysisSteps);

    const [queryDate, setQueryDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [valuation, setValuation] = useState('');
    const [currency, setCurrency] = useState('AED');
    const [toast, setToast] = useState(null);
    const [alertModal, setAlertModal] = useState({ show: false, message: '', stageCode: null, rfpId: null, rfpResData: null });

    const [fetchingGoNoGo, setFetchingGoNoGo] = useState(false);
    const [goNoGoData, setGoNoGoData] = useState(null);
    const [analysisRfpId, setAnalysisRfpId] = useState(null);
    const [analysisFullData, setAnalysisFullData] = useState(null);
    const [activeTab, setActiveTab] = useState('client-info');
    const [updatingDecision, setUpdatingDecision] = useState(false);
    const [selectedDecision, setSelectedDecision] = useState(null);

    // --- Template & Project selection state ---
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);
    const [selectAllProjects, setSelectAllProjects] = useState(false);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Fetch templates and projects on mount
    useEffect(() => {
        const fetchTemplates = async () => {
            setTemplatesLoading(true);
            try {
                const res = await apiGet('/templates');
                const data = res.data?.templates || [];
                setTemplates(data.map(t => ({
                    id: t.template_id,
                    name: t.name || t.client_name || 'Unnamed Template',
                })));
            } catch (e) {
                console.error('Failed to fetch templates:', e);
            } finally {
                setTemplatesLoading(false);
            }
        };

        const fetchProjects = async () => {
            setProjectsLoading(true);
            try {
                let res;
                try {
                    res = await api.get('/experience');
                } catch (err) {
                    res = await api.get('/projects');
                }
                const data = res.data?.projects || res.data || [];
                setProjects(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error('Failed to fetch projects:', e);
            } finally {
                setProjectsLoading(false);
            }
        };

        fetchTemplates();
        fetchProjects();
    }, []);

    const fetchGoNoGoData = async (rfp_id) => {
        try {
            setFetchingGoNoGo(true);
            const rfpRes = await api.get(`/rfp/${rfp_id}`);
            setAnalysisFullData(rfpRes.data);

            const goNoGoRes = await api.get(`/extraction/v1/${rfp_id}/go-no-go`);
            setGoNoGoData(goNoGoRes.data);
        } catch (rfpErr) {
            console.error('Error fetching go/no-go:', rfpErr);
            setToast({ message: "Analysis complete, but failed to fetch Go/No-Go data.", type: 'error' });
            setGoNoGoData({ error: 'Failed to fetch Go/No-Go decision.' });
        } finally {
            setFetchingGoNoGo(false);
        }
    };

    useEffect(() => {
        if (!analysisRfpId) return;

        const handleMessage = (payloadObj) => {
            const type = payloadObj.type || payloadObj.event;
            const payload = payloadObj.payload || payloadObj.data || payloadObj;

            // Allow both rfp_id and analysisRfpId to match
            const isMatchingRfp = String(payload?.rfpId) === String(analysisRfpId) || String(payload?.rfp_id) === String(analysisRfpId) || String(payload?.run_id) === String(currentRunId);

            if (type === 'EXTRACTION_PROGRESS' && isMatchingRfp) {
                const progress = payload?.progress || payload?.progress_percent || 0;
                const stepName = (payload?.step || '').toUpperCase();
                const currentMessage = payload?.message || '';

                setAnalysisProgress(progress);

                let stepIdx = 0;
                for (let i = 0; i < analysisSteps.length; i++) {
                    if (progress >= analysisSteps[i].percent) stepIdx = i;
                }
                setActiveStepIndex(stepIdx);

                if (currentMessage) {
                    setSteps(prevSteps => prevSteps.map((step, idx) => {
                        if (idx === stepIdx) {
                            return { ...step, title: currentMessage };
                        }
                        return step;
                    }));
                }

                if (progress >= 100 || stepName === 'COMPLETED') {
                    fetchGoNoGoData(payload?.rfp_id || analysisRfpId);
                    setCurrentRunId(null);
                }
            } else if (type === 'EXTRACTION_COMPLETED' && isMatchingRfp) {
                setAnalysisProgress(100);
                setActiveStepIndex(analysisSteps.length - 1);
                fetchGoNoGoData(payload?.rfp_id || analysisRfpId);
                setCurrentRunId(null);
            } else if (type === 'EXTRACTION_FAILED' && isMatchingRfp) {
                setAnalyzing(false);
                setAnalysisError(payload?.error || payload?.message || "Analysis failed.");
                setToast({ message: payload?.error || payload?.message || "Analysis failed. Please try again.", type: 'error' });
                setCurrentRunId(null);
            }
        };

        const unsubscribe = subscribeToMessages(handleMessage);
        return () => unsubscribe();
    }, [analysisRfpId, currentRunId, subscribeToMessages]);

    useEffect(() => {
        if (valuation) {
            setValuation(formatValuation(valuation, currency));
        }
    }, [currency]);

    const handleValuationChange = (val) => {
        let clean = val.replace(/[^0-9.,]/g, '');
        const parts = clean.split('.');
        if (parts.length > 2) {
            clean = parts[0] + '.' + parts.slice(1).join('');
        }

        const raw = clean.replace(/,/g, '');
        const formatted = formatValuation(raw, currency);
        setValuation(formatted);
    };

    const processFiles = (fileList) => {
        if (fileList.length === 0) return;
        setRawFiles(fileList);
        const selectedFiles = fileList.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
            type: file.name.split('.').pop().toUpperCase(),
        }));
        setFiles(selectedFiles);
    };

    const handleFileChange = (e) => {
        processFiles(Array.from(e.target.files));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) processFiles(droppedFiles);
    };

    const handleRemoveFile = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setFiles([]);
        setRawFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleStartAnalysis = async () => {
        const cleanValuation = valuation.replace(/,/g, '').trim();
        if (!/^\d+(\.\d+)?$/.test(cleanValuation)) {
            setToast({ message: 'Please enter a valid numeric valuation.', type: 'error' });
            return;
        }

        if (queryDate && dueDate && new Date(queryDate) > new Date(dueDate)) {
            setToast({ message: 'Query Submission date must be earlier than or equal to the Proposal Submission date.', type: 'error' });
            return;
        }

        setAnalyzing(true);
        setAnalysisError(null);
        setAnalysisProgress(0);
        setActiveStepIndex(0);
        setSteps(analysisSteps);

        try {
            const formData = new FormData();
            if (rawFiles && rawFiles.length > 0) {
                formData.append('file', rawFiles[0]);
            }
            formData.append('valuation', cleanValuation || '0');
            formData.append('currency', currency);
            if (dueDate) formData.append('manual_due_date', dueDate);
            if (queryDate) formData.append('query_deadline', queryDate);
            if (selectedTemplateId) formData.append('template_id', selectedTemplateId);
            if (selectAllProjects) {
                formData.append('project_ids', 'ALL');
            } else if (selectedProjectIds.length > 0) {
                formData.append('project_ids', selectedProjectIds.join(','));
            }

            const response = await api.post('/extraction/v1/extract', formData, {
                headers: { 'Content-Type': undefined }
            });

            if (response.status === 200 || response.status === 201) {
                const { run_id, rfp_id } = response.data;
                setAnalysisRfpId(rfp_id);
                setCurrentRunId(run_id);
            } else {
                setAnalyzing(false);
                setToast({ message: "Analysis failed. Please try again.", type: 'error' });
            }
        } catch (err) {
            setAnalyzing(false);
            setToast({ message: "Network error. Could not connect to the server.", type: 'error' });
        }
    };

    const handleGoNoGoSubmit = (stageCode) => {
        const rfpId = analysisRfpId;
        setAlertModal({
            show: true,
            message: stageCode === 8
                ? "Are you sure you want to move the proposal forward for further preparation and submission activities?"
                : "Are you sure you want to discontinue further proposal activities?",
            stageCode: stageCode,
            rfpId: rfpId,
            isConfirmation: true
        });
    };

    const handleConfirmGoNoGo = async () => {
        const { stageCode, rfpId } = alertModal;
        if (!rfpId) return;

        setUpdatingDecision(true);
        try {
            await api.patch(`/rfp/${rfpId}/proposal-stage`, { stage: stageCode, notes: "" });

            setAlertModal({ show: false, message: '', stageCode: null });

            if (stageCode === 8) {
                const rfpRes = await api.get(`/rfp/${rfpId}`);
                navigate(`/proposals/analysis-result/${rfpId}`, {
                    state: { analysisData: rfpRes.data, fromUpload: true, files: rawFiles }
                });
            } else {
                navigate('/proposals');
            }
        } catch (err) {
            console.error('Error submitting Go/No-Go decision:', err);
            setToast({ message: "Failed to update decision stage.", type: 'error' });
        } finally {
            setUpdatingDecision(false);
        }
    };

    return {
        navigate, fileInputRef, files, isDragging, analyzing, analysisProgress, activeStepIndex, steps,
        queryDate, setQueryDate, dueDate, setDueDate, valuation, setValuation, currency, setCurrency,
        toast, fetchingGoNoGo, goNoGoData, activeTab, setActiveTab, updatingDecision, selectedDecision,
        alertModal, setAlertModal, rawFiles,
        handleFileChange, handleDragOver, handleDragLeave, handleDrop, handleRemoveFile, handleStartAnalysis,
        handleGoNoGoSubmit, handleConfirmGoNoGo, isFormValid: files.length > 0 && valuation.trim() !== '',
        handleValuationChange,
        // Template & project selection
        templates, templatesLoading, selectedTemplateId, setSelectedTemplateId,
        projects, projectsLoading, selectedProjectIds, setSelectedProjectIds,
        selectAllProjects, setSelectAllProjects,
        setTemplates,  // exposed so caller can refresh list after creating a new template
    };
};
